import { toMonthlyPrice } from '@brand-lolly/app/billing/plan-interval/plan-interval.util';
import {
	PlanInterval,
	ProductAlias
} from '@brand-lolly/app/billing/types/billing.enums';
import { CatalogPlan } from '@brand-lolly/app/billing/types/plan.types';

/**
 * THE SELLING COPY ON AN APPLICATION TIER'S CARD — one line under the name, the
 * note under the price, and the label on the button.
 *
 * It lives here rather than in the catalogue because it is not catalogue data:
 * `plans` carries a title, a price, entitlements and limits, and nothing that
 * answers "why would I pick this one". The three cards say that in one sentence
 * each and the page renders them verbatim, so the sentence has exactly one home
 * and the two pages that show these cards cannot drift apart.
 *
 * Keyed on `tier_level` (0 free / 1 pro / 2 business), which is the tier — never
 * on the alias, which carries a ladder rung (`application_business_5000_yearly`)
 * and is not parseable back to one.
 *
 * WHAT PRO'S LINE USED TO SAY, because it is the one that moved. The signed-off
 * pricing artifact reads "Adds Partner Ads, so the creators and affiliates you
 * already work with can be backed by paid spend" — an assertion that Partner Ads
 * is what Pro buys, and therefore that a free account does not have it. The gate
 * behind that sentence was opened on 2026-09-15: Discovery, Affiliates and
 * Partner Ads are reachable on every tier, and only Social Listening is
 * withheld. A page that keeps selling a gate the product no longer has is a page
 * making a promise about a refusal that will never come, so the line was
 * rewritten around what Pro actually buys — its allowance, its ceilings, and the
 * insight tools the free tier does not carry.
 */
export interface PlanTierCopy {
	/**
	 * WHAT THE CARD'S HEADING SAYS — the tier, and nothing else.
	 *
	 * Not `Plan.title`, which is the catalogue's display name for one RUNG
	 * ("Lolly Platform — Pro — 100 credits/mo") and is wrong on a card three
	 * ways over: it repeats the product name the tab strip above already states,
	 * it repeats the allowance the dial two rows below is the control for, and —
	 * worst — it CHANGES AS THE DIAL MOVES, so a card's own heading would not
	 * hold still while a visitor compares tiers. The catalogue title is still
	 * the right string where a specific plan has to be named: the upgrade toast
	 * and the admin plans list both keep it.
	 */
	name: string;
	/** The one line under the tier's name. */
	subtitle: string;
	/** The note under the price. Empty where the price says it all. */
	priceNote: string;
	/** What the button says on the public pricing page. */
	cta: string;
}

const APPLICATION_TIER_COPY: ReadonlyArray<PlanTierCopy> = [
	{
		name: 'Free',
		subtitle: 'Find creators and send your first outreach.',
		priceNote: 'No card, no expiry',
		cta: 'Start free'
	},
	{
		name: 'Pro',
		subtitle:
			'More room to work: a bigger allowance, bigger searches and ad insight.',
		priceNote: '',
		cta: 'Choose Pro'
	},
	{
		name: 'Business',
		subtitle:
			'Adds Social Listening: track your brand, competitors and market.',
		priceNote: '',
		cta: 'Choose Business'
	}
];

/**
 * The quoted top of the ladder. Its title, price label, CTA and feature list all
 * come from `GET /billing/enterprise`; only the one line under the name and the
 * note under the price are this page's, so the four cards read as one set.
 */
export const ENTERPRISE_TIER_COPY: PlanTierCopy = {
	name: 'Enterprise',
	subtitle: 'Real-time monitoring, API access and the volume you need.',
	priceNote: 'Annual contract, invoiced',
	cta: 'Talk to us'
};

/** What the dial above the button is called, on every card that has one. */
export const ALLOWANCE_DIAL_LABEL = 'Monthly credits';

/** The dial's fixed reading on the quoted tier — there is nothing to move. */
export const ENTERPRISE_ALLOWANCE_LABEL = '5,000+ — quoted';

const APPLICATION_PLAN_PREFIX = `${ProductAlias.APPLICATION}_`;

/**
 * The copy for one plan's card, or `null` when the plan is not an application
 * tier.
 *
 * `null` is the right answer for the retired per-product plans a grandfathered
 * brand still sees on the settings page: their cards render exactly as they did
 * before this file existed, because a Social Listening "Professional" tier has
 * no sentence here and inventing one would be worse than leaving the row out.
 */
export function applicationTierCopy(plan: CatalogPlan): PlanTierCopy | null {
	if (!plan.alias.startsWith(APPLICATION_PLAN_PREFIX)) {
		return null;
	}
	return APPLICATION_TIER_COPY[plan.tier_level] ?? null;
}

/**
 * The heading a plan's card carries: the tier alone on an application plan, and
 * the catalogue's own title on anything else.
 *
 * `null` is not an option here the way it is for {@link applicationTierCopy} —
 * every card needs a heading, and a retired per-product plan's catalogue title
 * ("Social Listening — Professional") is already the right one for it.
 */
export function planCardTitle(plan: CatalogPlan): string {
	return applicationTierCopy(plan)?.name ?? plan.title;
}

/**
 * A price figure as the cards print one — no currency mark: grouped, and without
 * the cents when there are none. `"1290.00"` → `"1,290"`, `"27.50"` → `"27.50"`.
 *
 * Every application price a brand actually pays is a whole number of dollars by
 * construction — the seed rounds each rung before multiplying it for the year —
 * so `.00` was two characters of noise on the loudest thing on the card. A
 * yearly rung's PER-MONTH figure does divide unevenly ($330 a year is $27.50 a
 * month) and keeps its cents, because rounding it to $28 would print a number
 * nobody is charged and would not multiply back to the annual total beside it.
 */
export function planPriceFigure(price: string): string {
	const value = parseFloat(price);
	if (isNaN(value)) {
		return price;
	}
	const fraction = value % 1 === 0 ? 0 : 2;
	return value.toLocaleString('en-US', {
		minimumFractionDigits: fraction,
		maximumFractionDigits: fraction
	});
}

/** {@link planPriceFigure} with the currency mark, for prose and for a note. */
export function formatPlanPrice(price: string): string {
	const figure = planPriceFigure(price);
	return figure === price && isNaN(parseFloat(price)) ? price : `$${figure}`;
}

/**
 * The note under the price on a YEARLY plan — "Billed annually — $1,290 a year".
 *
 * The card's figure is always a per-month one, so without this the annual view
 * shows a number the brand is never charged and never states the one it is.
 * `null` on a monthly plan, which lets the tier's own note take the row.
 */
export function annualBillingNote(plan: CatalogPlan): string | null {
	if (plan.interval !== PlanInterval.YEAR) {
		return null;
	}
	return `Billed annually — ${formatPlanPrice(plan.price)} a year`;
}

/**
 * One rung as the dial reads it: "400 credits — $129/mo".
 *
 * THE PRICE IS IN THE OPTION, and that is the whole reason the rungs are not
 * bare credit counts. The dial is the only place a brand compares rungs, and a
 * list of allowances with no prices beside them asks it to open five of them in
 * turn to find out what each costs.
 *
 * THIS ONE-LINE READING IS NO LONGER WHAT THE OPEN PANEL DRAWS. The dial builds
 * each option from `PlanCardLadderStop`'s separate parts, so the saving can sit
 * between the allowance and the price as its own pill. What is left for this
 * string is the two places that genuinely have one run of text and nowhere to
 * put a pill: a single-rung dial's fixed reading, and the name the plan-details
 * dialog gives the rung it is describing.
 *
 * A rung stating no allowance falls back to its plan title: a plan the catalogue
 * cannot describe should still be selectable.
 */
export function ladderStopLabel(
	plan: CatalogPlan,
	credits: number | null
): string {
	if (credits === null) {
		return plan.title;
	}
	const allowance = `${credits.toLocaleString('en-US')}`;
	// The free tier has one rung and it costs nothing, so "— $0/mo" is a price
	// nobody is comparing against anything. It states the allowance instead.
	if (parseFloat(plan.price) === 0) {
		return `${allowance} monthly credits`;
	}
	return `${allowance} credits — ${formatPlanPrice(toMonthlyPrice(plan))}/mo`;
}

/**
 * THE LINE UNDER THE RUNGS — where to go when the top of this ladder is not
 * enough. "More than 1,200? Try Business" on Pro; "More than 5,000? Talk to us"
 * on Business.
 *
 * The number is READ OFF THE TOP RUNG rather than written here, so a seed that
 * adds a rung cannot leave the page pointing at a ceiling the tier no longer
 * has. The destination is keyed on `tier_level` the same way the rest of this
 * file is: 1 (Pro) is sent up a tier, 2 (Business) is sent to the quoted one,
 * and every other tier — the free one, and any non-application plan — gets no
 * line at all, because a card with one rung has no ceiling to exceed.
 *
 * Rendered as prose, not as a control: "Talk to us" is already the Enterprise
 * card's own button on the same page, and a second thing by that name that did
 * something different would be the worse kind of duplication.
 */
export function ladderFooterNote(
	rungs: CatalogPlan[],
	creditsOf: (plan: CatalogPlan) => number | null
): string {
	const top = rungs[rungs.length - 1];
	if (!top || rungs.length < 2 || !applicationTierCopy(top)) {
		return '';
	}
	const ceiling = creditsOf(top);
	if (ceiling === null) {
		return '';
	}
	const allowance = ceiling.toLocaleString('en-US');
	if (top.tier_level === 1) {
		return `More than ${allowance}? Try Business`;
	}
	if (top.tier_level === 2) {
		return `More than ${allowance}? Talk to us`;
	}
	return '';
}
